// Pure helpers for the Findings page: severity order, status groups, search and grouping the same problem together.
export const SEVERITIES = ['critical', 'high', 'medium', 'low', 'info'];
export const SEV_RANK = Object.fromEntries(SEVERITIES.map((s, i) => [s, i]));
export const CLOSED = ['resolved', 'false_positive', 'accepted_risk'];

// Status chips above the list. `open` means anything still needing work.
export const STATUS_TABS = [
  { id: 'open', label: 'Open', test: (f) => !CLOSED.includes(f.status) },
  { id: 'confirmed', label: 'Confirmed', test: (f) => f.status === 'confirmed' },
  { id: 'resolved', label: 'Resolved', test: (f) => f.status === 'resolved' },
  { id: 'false_positive', label: 'False positive', test: (f) => f.status === 'false_positive' },
  { id: 'accepted_risk', label: 'Accepted risk', test: (f) => f.status === 'accepted_risk' }
];

// The four decisions offered on a finding, with the status each one sets.
export const DECISIONS = [
  { status: 'confirmed', label: 'Confirm', primary: true },
  { status: 'resolved', label: 'Mark resolved' },
  { status: 'false_positive', label: 'False positive' },
  { status: 'accepted_risk', label: 'Accept risk' }
];

export const whereOf = (f) => [f.host, f.port].filter(Boolean).join(':') || '-';

const order = (a, b) => (SEV_RANK[a.severity] ?? 9) - (SEV_RANK[b.severity] ?? 9) || (b.cvss_score || 0) - (a.cvss_score || 0) || new Date(b.created_at) - new Date(a.created_at);

// Where an imported scan was run from. Findings without a label come from older imports or FuzzScan's own scans.
export const SCOPES = [
  { id: 'all', label: 'All' },
  { id: 'external', label: 'External', color: '#38BDF8' },
  { id: 'internal', label: 'Internal', color: '#C084FC' }
];
export const SCOPE_COLOR = { external: '#38BDF8', internal: '#C084FC' };
export const SCOPE_LABEL = { external: 'External', internal: 'Internal' };
export const inScope = (list, scope) => (scope === 'all' ? list : list.filter((f) => (f.scope || '') === scope));
// Open findings per scope, for the numbers on the scope tabs.
export const scopeCounts = (list) => {
  const live = list.filter((f) => !f.is_duplicate && !CLOSED.includes(f.status));
  return { all: live.length, external: live.filter((f) => f.scope === 'external').length, internal: live.filter((f) => f.scope === 'internal').length };
};

export function filterFindings(list, { tab, severity, q }) {
  const t = STATUS_TABS.find((x) => x.id === tab) || STATUS_TABS[0];
  const s = q.trim().toLowerCase();
  return list.filter((f) => !f.is_duplicate && t.test(f) && (severity === 'all' || f.severity === severity)
    && (!s || `${f.title} ${f.host || ''} ${f.cve_id || ''}`.toLowerCase().includes(s))).sort(order);
}

// Severity counters for what is still open (ignores the severity filter so the numbers stay put).
export const openBySeverity = (list) => Object.fromEntries(SEVERITIES.map((s) => [s, list.filter((f) => !f.is_duplicate && f.severity === s && !CLOSED.includes(f.status)).length]));
export const tabCounts = (list) => Object.fromEntries(STATUS_TABS.map((t) => [t.id, list.filter((f) => !f.is_duplicate && t.test(f)).length]));

// Same title = same problem. Worst severity first, then biggest group. Single findings stay as plain rows.
export function groupFindings(list) {
  const map = new Map();
  list.forEach((f) => { const k = f.title.trim().toLowerCase(); (map.get(k) || map.set(k, []).get(k)).push(f); });
  return [...map.values()].map((items) => ({ key: items[0].id, title: items[0].title, severity: items[0].severity, items }))
    .sort((a, b) => (SEV_RANK[a.severity] ?? 9) - (SEV_RANK[b.severity] ?? 9) || b.items.length - a.items.length);
}
